
import React, { useState, useEffect, useRef } from 'react';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Grid3X3, ArrowRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { caseStudies } from '@/data/caseStudies';
import PageHeader from '@/components/PageHeader';

// Define project types
interface Project {
  id: string;
  title: string;
  category: string;
  description: string;
  imageUrl: string;
  link: string;
}

// Add placeholder images to case studies that don't have images
const portfolioImages = [
  "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1518770660439-4636190af475?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1649972904349-6e44c42644a7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80"
];

// Make sure all case studies have images by adding placeholder images where needed
const enhancedCaseStudies = caseStudies.map((study, index) => {
  if (!study.imageUrl || study.imageUrl === '/placeholder.svg') {
    return {
      ...study,
      imageUrl: portfolioImages[index % portfolioImages.length]
    };
  }
  return study;
});

// Extract unique categories
const categories = ["All", ...Array.from(new Set(enhancedCaseStudies.map(project => project.category)))];

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [filteredProjects, setFilteredProjects] = useState(enhancedCaseStudies);
  const portfolioRef = useRef<HTMLDivElement>(null);

  // Filter projects when category changes
  useEffect(() => {
    if (activeCategory === "All") {
      setFilteredProjects(enhancedCaseStudies);
    } else {
      setFilteredProjects(enhancedCaseStudies.filter(project => project.category === activeCategory));
    }
  }, [activeCategory]);

  // Animation on scroll and scroll to top
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, {
      threshold: 0.1
    });

    if (portfolioRef.current) {
      observer.observe(portfolioRef.current);
    }

    return () => {
      if (portfolioRef.current) {
        observer.unobserve(portfolioRef.current);
      }
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />

      <main className="flex-grow">
        {/* Hero Section with black background and wave */}
        <PageHeader
          icon={<Grid3X3 size={16} />}
          badgeText="Our Portfolio"
          title={<>We Let Our Work <span className="text-gold">Speak for Itself.</span></>}
          subtitle="Luxury. Performance. Affordability. See how our AI-powered development process has 
          transformed businesses—one pixel-perfect app at a time."
        />

        {/* Portfolio Grid Section */}
        <section
          ref={portfolioRef}
          style={{ opacity: 0 }}
          className="py-20 px-6 md:px-10 bg-white"
        >
          <div className="container mx-auto">
            <div className="mb-12">
              <Tabs defaultValue="All" className="w-full">
                <TabsList className="w-full flex flex-wrap justify-center mb-8 bg-luxury-50/50">
                  {categories.map(category => (
                    <TabsTrigger
                      key={category}
                      value={category}
                      onClick={() => setActiveCategory(category)}
                      className="data-[state=active]:bg-highlight data-[state=active]:text-white"
                    >
                      {category}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value={activeCategory} className="mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredProjects.map(project => (
                      <Link to={project.link} key={project.id} className="group" onClick={() => window.scrollTo(0, 0)}>
                        <Card className="overflow-hidden border border-luxury-100 hover:shadow-luxury transition-all duration-300 h-full">
                          <div className="relative overflow-hidden aspect-video">
                            <img
                              src={project.imageUrl}
                              alt={project.title}
                              className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = '/placeholder.svg';
                              }}
                            />
                            <div className="absolute inset-0 bg-luxury-900/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                              <ExternalLink className="text-white w-10 h-10" />
                            </div>
                          </div>

                          <CardContent className="p-6">
                            <div className="text-sm text-highlight font-medium mb-2">
                              {project.category}
                            </div>
                            <h3 className="text-xl font-bold text-luxury-900 mb-2 group-hover:text-highlight transition-colors">
                              {project.title}
                            </h3>
                            <p className="text-luxury-600 mb-4">
                              {project.description}
                            </p>
                            <div className="text-highlight font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <span>View Case Study</span>
                              <ArrowRight className="h-4 w-4" />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <div className="text-center mt-12">
              <Button size="lg" className="bg-highlight hover:bg-highlight-dark text-white premium-button">
                View More Projects
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Portfolio;
